import {
  useAllInvestmentAccountsPerformanceQuery,
  useAllInvestmentAccountsQuery,
  useInvestmentGapsQuery,
  usePortfolioPerformanceSummaryQuery,
  usePortfolioXirrQuery,
} from '@transactions/graphql';
import {Plus, Search} from 'lucide-react';
import * as React from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {
  ACCOUNT_TYPE_OPTIONS,
  isAccountClosed,
  matchesAccountFilter,
  matchesAccountTypeFilter,
  parseAccountFilter,
  parseAccountTypeFilter,
  parseExplicitAccountFilter,
  serializeAccountTypeFilter,
} from 'src/AccountsView/accountFilter';
import {Button} from 'src/Button';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {AccountV2Icon} from 'src/icons/AccountV2Icon';
import {InvestmentFleet} from 'src/InvestmentAccountsView/InvestmentFleet';
import {PortfolioChart} from 'src/InvestmentAccountsView/PortfolioChart';
import {balancedThroughByAccount, balanceStatusText} from 'src/InvestmentAccountsView/reconcileModel';
import {logoUrl} from 'src/logoOptions';
import {useModals} from 'src/Modals';
import type {
  AllInvestmentAccountsPerformanceQuery,
  AllInvestmentAccountsQuery,
} from '@transactions/graphql';
import type {
  AccountFilter,
  AccountTypeFilter,
} from 'src/AccountsView/accountFilter';

// The investments overview only surfaces investment-type filters (the banking
// subtypes never appear here).
const INVESTMENT_TYPE_OPTIONS = ACCOUNT_TYPE_OPTIONS.filter(
  (option) => option.group === 'Investments',
);

// The overview intentionally loads in two phases: the slim account list
// (fast, paints the page) and per-account performance (slow, streams in).
type InvestmentAccount =
  AllInvestmentAccountsQuery['allInvestmentAccounts'][number];
type AccountPerformance =
  AllInvestmentAccountsPerformanceQuery['allInvestmentAccounts'][number]['performance'];

const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatPercent = (decimal: number | null | undefined): string => {
  if (decimal == null) return '-';
  const sign = decimal >= 0 ? '+' : '';
  return `${sign}${(decimal * 100).toFixed(2)}%`;
};

const gainColor = (value: number | null | undefined): string =>
  value != null && value > 0
    ? 'text-positive'
    : value != null && value < 0
      ? 'text-red-600 dark:text-red-400'
      : 'text-muted-foreground';

const PERIOD_LABELS: Record<string, string> = {
  '1W': '1 week',
  '1M': '1 month',
  YTD: 'Year to date',
  '1Y': '1 year',
};

type AccountRowProps = {
  account: InvestmentAccount;
  performance: AccountPerformance | undefined;
  /** When its checks don't balance, from when (else nothing). */
  balanceStatus: string | undefined;
};

const getAccountSubtitle = (account: InvestmentAccount): string => {
  // V2 accounts use plaid.institutionName for Plaid accounts
  if (account.plaid?.institutionName) {
    return account.plaid.institutionName;
  }
  // Manual investment account
  return 'Manual investment';
};

const AccountRow = ({account, performance, balanceStatus}: AccountRowProps) => {
  const gain = performance?.totalGain;
  const xirr = performance?.xirr;
  const isPositive = gain != null && gain > 0;
  const isNegative = gain != null && gain < 0;
  // Closed rows surface in the widened search/type-filter results (and the
  // explicit Closed view), so they carry the same marks as the main
  // accounts list: dimmed identity plus a named state.
  const closed = isAccountClosed(account);

  return (
    <Link
      to={`/accounts/${account._id}`}
      className="border-border bg-card hover:bg-accent flex items-center gap-3 rounded-lg border p-4 no-underline transition-colors"
    >
      {account.logo ? (
        <img
          src={logoUrl(account.logo)}
          alt=""
          className={`h-6 w-6 rounded ${closed ? 'opacity-50 grayscale' : ''}`}
        />
      ) : (
        <AccountV2Icon
          account={account}
          className={`size-6 shrink-0 ${closed ? 'opacity-50 grayscale' : ''}`}
        />
      )}
      <div className="flex flex-1 flex-col">
        <span
          className={`font-medium ${
            closed ? 'text-muted-foreground' : 'text-foreground'
          }`}
        >
          {account.name}
        </span>
        <span className="text-muted-foreground text-sm">
          {getAccountSubtitle(account)}
          {closed && ' · Closed'}
        </span>
        {balanceStatus != null && <span className="text-warning text-sm">{balanceStatus}</span>}
      </div>
      <div className="flex flex-col items-end">
        <span className="text-foreground font-medium">
          {formatCurrency(account.presentBalance)}
        </span>
        <div className="flex items-center gap-2 text-sm">
          {gain != null && (
            <span
              className={
                isPositive
                  ? 'text-positive'
                  : isNegative
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground'
              }
            >
              {isPositive ? '+' : ''}
              {formatCurrency(gain)}
            </span>
          )}
          {xirr != null && (
            <span
              className={
                xirr > 0
                  ? 'text-positive'
                  : xirr < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground'
              }
            >
              {formatPercent(xirr)} XIRR
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export const InvestmentAccountsListView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = parseAccountTypeFilter(searchParams.get('type'));
  // Search lives in the URL like the status/type filters, so it survives
  // navigation (opening an account and hitting back keeps the search).
  const search = searchParams.get('search') ?? '';
  // Contextual status default, matching the accounts overview: browsing
  // defaults to 'active', but narrowing by name or type widens to 'all' so
  // a searched-for closed account can't be hidden by a filter nobody set.
  // An explicit pick always wins.
  const explicitFilter = parseExplicitAccountFilter(searchParams.get('filter'));
  const defaultFilter: AccountFilter =
    search.length > 0 || typeFilter.length > 0 ? 'all' : 'active';
  const filter = explicitFilter ?? defaultFilter;
  const {data, loading, refetch} = useAllInvestmentAccountsQuery();
  // Quiet when an account's checks balance; otherwise the last day they did.
  const {
    data: gapsData,
    error: gapsError,
    refetch: refetchGaps,
  } = useInvestmentGapsQuery({fetchPolicy: 'cache-and-network'});
  const {data: accountPerformanceData, refetch: refetchAccountPerformance} =
    useAllInvestmentAccountsPerformanceQuery();
  const {data: performanceData} = usePortfolioPerformanceSummaryQuery();
  // The resolver's default path is present-observation aware (per-account
  // windows), matching the today-clamped total beside it.
  const {data: xirrData} = usePortfolioXirrQuery();
  const {createAccountModal} = useModals();

  const setFilter = (newFilter: AccountFilter) => {
    const next = new URLSearchParams(searchParams);
    // The param is only present for a non-default pick; the default is
    // contextual, so an explicit Active during a search must be kept.
    if (newFilter === defaultFilter) {
      next.delete('filter');
    } else {
      next.set('filter', newFilter);
    }
    setSearchParams(next);
  };

  const setTypeFilter = (newTypeFilter: AccountTypeFilter) => {
    const next = new URLSearchParams(searchParams);
    const serialized = serializeAccountTypeFilter(newTypeFilter);
    if (serialized == null) {
      next.delete('type');
    } else {
      next.set('type', serialized);
    }
    setSearchParams(next);
  };

  const setSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === '') {
      next.delete('search');
    } else {
      next.set('search', value);
    }
    // replace: the search box updates per keystroke, so pushing would bury the
    // previous page under one history entry per character typed.
    setSearchParams(next, {replace: true});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">
          Loading investment accounts...
        </span>
      </div>
    );
  }

  const rawAccounts = data?.allInvestmentAccounts ?? [];

  // Cross-counts, GitHub-PR-list style: each dropdown's counts respect the
  // OTHER filter (with type=IRA selected, "Closed 2" means 2 closed IRAs).
  const typeMatchedAccounts = rawAccounts.filter((account) =>
    matchesAccountTypeFilter(typeFilter, account),
  );
  const statusMatchedAccounts = rawAccounts.filter((account) =>
    matchesAccountFilter(filter, account),
  );
  const visibleAccounts = typeMatchedAccounts.filter((account) =>
    matchesAccountFilter(filter, account),
  );
  const activeCount = typeMatchedAccounts.filter(
    (a) => !isAccountClosed(a),
  ).length;

  const statusOptions = [
    {key: 'active', label: 'Active', count: activeCount},
    {
      key: 'closed',
      label: 'Closed',
      count: typeMatchedAccounts.length - activeCount,
    },
  ];
  // Only types with at least one account are offered (plus current
  // selections, so they can always be cleared)
  const typeOptions = INVESTMENT_TYPE_OPTIONS.map((option) => ({
    key: option.key,
    label: option.label,
    count: statusMatchedAccounts.filter(option.matches).length,
    present: rawAccounts.some(option.matches),
  })).filter((option) => option.present || typeFilter.includes(option.key));

  const accounts = visibleAccounts
    .filter((account) =>
      (account.name || '').toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => b.presentBalance - a.presentBalance);

  // The portfolio summary and history describe the whole portfolio, so hide
  // them while a status/type filter is slicing the list — a pooled XIRR next
  // to a filtered subset would misread.
  const isFiltering = filter !== 'active' || typeFilter.length > 0;
  // Only gate the summary on there being active accounts; the numbers below
  // themselves cover the whole portfolio (see totals).
  const activeAccounts = rawAccounts.filter(
    (account) => !isAccountClosed(account),
  );

  // Per-account performance arrives after the account list; until then rows
  // and totals show balances only and the gain/XIRR figures stream in.
  const performanceByAccountId = new Map(
    (accountPerformanceData?.allInvestmentAccounts ?? []).map((a) => [
      a._id,
      a.performance,
    ]),
  );
  const performanceLoaded = accountPerformanceData != null;
  // Unknown when the gaps didn't load (or failed to refresh): no account
  // reads as balanced, or as still unbalanced, on old or missing data.
  const balancedThrough = balancedThroughByAccount(gapsData?.investmentGaps ?? []);
  const balanceStatusOf = (accountId: string) =>
    gapsError == null && balancedThrough.has(accountId)
      ? balanceStatusText(balancedThrough.get(accountId) ?? null)
      : undefined;

  // Totals cover the whole portfolio (every investment account, including
  // closed ones) so they stay consistent with portfolioXirr, the period
  // summary, and the chart — all of which aggregate server-side over all
  // accounts. Closed accounts are wound down to a zero balance, so they add
  // nothing to totalValue; their historical cost basis still counts toward
  // totalGain, matching the pooled XIRR. XIRR is the money-weighted return
  // across all cash flows — not gain / net contributions, which is
  // meaningless once any account has withdrawn more than it deposited.
  // Present (today-clamped) values, so the header can't disagree with the
  // fleet cards when a future-dated anchor exists.
  const totalValue = rawAccounts.reduce((sum, a) => sum + a.presentBalance, 0);
  const totalCostBasis = rawAccounts.reduce(
    (sum, a) => sum + (performanceByAccountId.get(a._id)?.costBasis ?? 0),
    0,
  );
  const totalGain = totalValue - totalCostBasis;
  const portfolioXirr = xirrData?.portfolioXIRR.rate ?? null;
  const periods = performanceData?.portfolioPerformanceSummary ?? [];

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex flex-col gap-4 p-4">
        {/* Status/type filter, GitHub-PR-list style */}
        {rawAccounts.length > 0 && (
          <div className="flex items-center gap-2">
            <FilterDropdown
              label="Status"
              allLabel="All"
              allCount={typeMatchedAccounts.length}
              value={filter}
              onChange={(key) => setFilter(parseAccountFilter(key))}
              options={statusOptions}
            />
            <FilterDropdown
              label="Type"
              allLabel="All types"
              allCount={statusMatchedAccounts.length}
              multiple
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
            />
          </div>
        )}

        {/* Portfolio summary */}
        {!isFiltering && activeAccounts.length > 0 && (
          <div className="border-border bg-card rounded-lg border p-4">
            <div className="text-muted-foreground mb-1 text-sm">
              Total portfolio value
            </div>
            <div className="text-foreground text-2xl font-semibold">
              {formatCurrency(totalValue)}
            </div>
            <div className="flex gap-2 text-sm">
              {performanceLoaded && (
                <span className={gainColor(totalGain)}>
                  {totalGain > 0 ? '+' : ''}
                  {formatCurrency(totalGain)} total gain
                </span>
              )}
              {portfolioXirr != null && (
                <span className={gainColor(portfolioXirr)}>
                  {formatPercent(portfolioXirr)} XIRR
                </span>
              )}
            </div>
            {periods.length > 0 && (
              <div className="border-border mt-4 grid grid-cols-2 gap-3 border-t pt-4 sm:grid-cols-4">
                {periods.map((period) => (
                  <div key={period.key}>
                    <div className="text-muted-foreground text-xs">
                      {PERIOD_LABELS[period.key] ?? period.key}
                    </div>
                    <div
                      className={`font-medium ${gainColor(period.gainChange)}`}
                    >
                      {period.returnPercent != null
                        ? formatPercent(period.returnPercent)
                        : '—'}
                    </div>
                    <div
                      className={`text-xs ${gainColor(period.gainChange)}`}
                    >
                      {period.gainChange == null
                        ? '—'
                        : `${period.gainChange > 0 ? '+' : ''}${formatCurrency(period.gainChange)}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Portfolio history chart */}
        {!isFiltering && activeAccounts.length > 0 && <PortfolioChart />}

        {/* Search and create */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-border bg-background placeholder:text-muted-foreground focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
          <Button
            variant="outline"
            onClick={() =>
              createAccountModal.openModal({
                role: 'investment',
                onCreate: () => {
                  void refetch();
                  void refetchAccountPerformance();
                },
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Create
          </Button>
        </div>

        {gapsError != null && (
          <div
            role="alert"
            className="bg-destructive/10 text-destructive flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm"
          >
            <span>Couldn&apos;t check balances.</span>
            <button type="button" className="underline" onClick={() => void refetchGaps().catch(() => undefined)}>
              Retry
            </button>
          </div>
        )}

        {/* Account list: the fleet card view is the unfiltered browse
            layout; any search or status/type filter flattens back to the
            plain row list, which keeps the widened closed-account search
            behavior intact. */}
        {rawAccounts.length === 0 ? (
          <div className="border-border rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground mb-4">
              No investment accounts connected
            </p>
            <p className="text-muted-foreground mb-4 text-sm">
              Connect an investment account through Plaid to track your
              portfolio performance.
            </p>
          </div>
        ) : !isFiltering && search.length === 0 && activeAccounts.length > 0 ? (
          <InvestmentFleet
            accounts={activeAccounts}
            performanceByAccountId={performanceByAccountId}
            balanceStatusOf={balanceStatusOf}
          />
        ) : accounts.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center text-sm">
            {search.length > 0
              ? `No accounts matching "${search}"`
              : typeFilter.length === 0
                ? `No ${filter} accounts`
                : 'No matching accounts'}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {accounts.map((account) => (
              <AccountRow
                key={account._id}
                account={account}
                performance={performanceByAccountId.get(account._id)}
                balanceStatus={balanceStatusOf(account._id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
